/*
  Launch: collection carousel. Scrolling itself is native (scroll snap, touch,
  trackpad). This file only wires the arrow buttons and the Left and Right
  keys to move one card at a time, disables each arrow at its end, and
  optionally fits each product photo to its frame (see initFit).
*/
(function () {
  'use strict';

  var reduceMotion = window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function init(root) {
    if (root.__launchCarousel) return;
    root.__launchCarousel = true;

    var track = root.querySelector('[data-launch-carousel-track]');
    if (!track) return;
    initFit(root);
    var prev = root.querySelector('[data-launch-carousel-prev]');
    var next = root.querySelector('[data-launch-carousel-next]');

    function step() {
      var item = track.querySelector('.launch-carousel__item');
      if (!item) return track.clientWidth;
      var gap = parseFloat(getComputedStyle(track).columnGap) || 0;
      return item.getBoundingClientRect().width + gap;
    }

    function move(dir) {
      track.scrollBy({ left: dir * step(), behavior: reduceMotion ? 'auto' : 'smooth' });
    }

    function update() {
      var max = track.scrollWidth - track.clientWidth;
      var x = Math.abs(track.scrollLeft);
      if (prev) prev.disabled = x <= 1;
      if (next) next.disabled = x >= max - 1;
    }

    if (prev) prev.addEventListener('click', function () { move(-1); });
    if (next) next.addEventListener('click', function () { move(1); });

    track.addEventListener('keydown', function (e) {
      if (e.altKey || e.ctrlKey || e.metaKey) return;
      if (e.key === 'ArrowRight') { e.preventDefault(); move(1); }
      else if (e.key === 'ArrowLeft') { e.preventDefault(); move(-1); }
    });

    var ticking = false;
    track.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(function () { ticking = false; update(); });
    }, { passive: true });

    if ('ResizeObserver' in window) new ResizeObserver(update).observe(track);
    else window.addEventListener('resize', update);
    update();
  }

  /*
    Optional product fit (section setting "Fit each product to the frame").
    Packshots ship with different amounts of white space, so one zoom value
    cannot size them all alike. For each loaded image this reads a small copy
    of the pixels (same-origin store CDN), finds the box of non-white pixels,
    and sets --l-img-zoom / --l-img-tx / --l-img-ty so the longest side of the
    product is `size` percent of the frame, centered. Images whose corners are
    not white, or whose pixels cannot be read, keep the CSS zoom and shift.
  */
  var FIT_PX = 200;
  var INK = 235;

  function measure(img) {
    var iw = img.naturalWidth, ih = img.naturalHeight;
    if (!iw || !ih) return null;
    var k = Math.min(1, FIT_PX / Math.max(iw, ih));
    var w = Math.max(1, Math.round(iw * k)), h = Math.max(1, Math.round(ih * k));
    var c = document.createElement('canvas');
    c.width = w; c.height = h;
    var ctx = c.getContext('2d', { willReadFrequently: true });
    if (!ctx) return null;
    ctx.drawImage(img, 0, 0, w, h);
    var d;
    try { d = ctx.getImageData(0, 0, w, h).data; } catch (e) { return null; }
    function low(i) { return Math.min(d[i], d[i + 1], d[i + 2]); }
    var corners = [0, (w - 1) * 4, (h - 1) * w * 4, ((h - 1) * w + w - 1) * 4];
    for (var q = 0; q < 4; q++) if (low(corners[q]) < 245) return null;
    var x0 = w, y0 = h, x1 = -1, y1 = -1;
    for (var y = 0; y < h; y++) {
      for (var x = 0; x < w; x++) {
        if (low((y * w + x) * 4) < INK) {
          if (x < x0) x0 = x; if (x > x1) x1 = x;
          if (y < y0) y0 = y; if (y > y1) y1 = y;
        }
      }
    }
    if (x1 < 0) return null;
    return { x0: x0 / w, x1: (x1 + 1) / w, y0: y0 / h, y1: (y1 + 1) / h, ar: iw / ih };
  }

  function fit(img, size) {
    var box = img.parentNode;
    var bw = box.clientWidth, bh = box.clientHeight;
    if (!bw || !bh) return;
    var m = measure(img);
    if (!m) return;
    // Where the photo is drawn inside the frame (object-fit cover or contain, centered).
    var contain = getComputedStyle(img).objectFit === 'contain';
    var s = (contain ? Math.min : Math.max)(bw / m.ar, bh);
    var rw = s * m.ar, rh = s;
    var ox = (bw - rw) / 2, oy = (bh - rh) / 2;
    var cx0 = ox + m.x0 * rw, cx1 = ox + m.x1 * rw;
    var cy0 = oy + m.y0 * rh, cy1 = oy + m.y1 * rh;
    var span = Math.max((cx1 - cx0) / bw, (cy1 - cy0) / bh);
    if (span <= 0) return;
    var z = (size / 100) / span;
    var tx = -(((cx0 + cx1) / 2) / bw - 0.5) * z * 100;
    var ty = -(((cy0 + cy1) / 2) / bh - 0.5) * z * 100;
    img.style.setProperty('--l-img-zoom', z.toFixed(4));
    img.style.setProperty('--l-img-tx', tx.toFixed(3) + '%');
    img.style.setProperty('--l-img-ty', ty.toFixed(3) + '%');
  }

  function initFit(root) {
    var size = parseFloat(root.getAttribute('data-launch-carousel-fit'));
    if (!(size > 0)) return;
    size = Math.min(100, size);
    var imgs = root.querySelectorAll('.launch-carousel__media > img');
    for (var i = 0; i < imgs.length; i++) {
      (function (img) {
        function run() { try { fit(img, size); } catch (e) { /* keep the CSS zoom */ } }
        // A srcset swap loads a new file: measure again.
        img.addEventListener('load', run);
        if (img.complete && img.naturalWidth) run();
      })(imgs[i]);
    }
  }

  function initAll(scope) {
    var roots = (scope || document).querySelectorAll('[data-launch-carousel]');
    for (var i = 0; i < roots.length; i++) init(roots[i]);
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', function () { initAll(); });
  else initAll();

  document.addEventListener('shopify:section:load', function (e) { initAll(e.target); });
})();
